'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

interface Service {
  id: string;
  icon: string;
  title: string;
  tagline: string;
  description: string;
  features: string[];
  category: string;
}

const services: Service[] = [
  {
    id: 'it-staffing',
    icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z',
    title: 'IT Staffing Solutions',
    tagline: 'Right talent, right time, every time',
    description: 'Our core IT staffing service connects Fortune 500 companies with pre-vetted technology professionals across all disciplines. We leverage a proprietary talent network of 200,000+ IT professionals to deliver qualified candidates faster than any competitor.',
    features: [
      'Contract & Contract-to-Hire placements',
      'Direct hire and permanent placement',
      'Project-based team augmentation',
      'Rapid deployment within 48-72 hours',
      'Technical skills assessment & vetting',
      'Background checks & compliance management',
    ],
    category: 'Staffing',
  },
  {
    id: 'executive-search',
    icon: 'M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
    title: 'Executive Search',
    tagline: 'Leadership that transforms organizations',
    description: 'Our executive search practice specializes in identifying and recruiting senior technology leaders — CIOs, CTOs, VPs of Engineering, and other C-suite technology executives who drive digital transformation at the highest levels.',
    features: [
      'CIO, CTO & VP-level placements',
      'Confidential search capabilities',
      'Comprehensive candidate assessment',
      'Cultural fit evaluation',
      'Market intelligence & compensation benchmarking',
      '12-month placement guarantee',
    ],
    category: 'Executive',
  },
  {
    id: 'contract-staffing',
    icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01',
    title: 'Contract Staffing',
    tagline: 'Flexible workforce solutions at scale',
    description: 'Scale your technology workforce up or down with our flexible contract staffing solutions. Whether you need one specialist or an entire project team, we deliver the right professionals with the right skills on your timeline.',
    features: [
      'Short-term and long-term contracts',
      'Full project team deployment',
      'Managed services options',
      'Payroll and benefits administration',
      'Compliance and risk management',
      'Seamless onboarding support',
    ],
    category: 'Staffing',
  },
  {
    id: 'direct-hire',
    icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
    title: 'Direct Hire Placement',
    tagline: 'Build your permanent technology team',
    description: 'Our direct hire service focuses on finding the perfect permanent addition to your technology team. We go beyond technical skills to evaluate leadership potential, cultural alignment, and long-term career trajectory.',
    features: [
      'Full-time permanent placements',
      'Deep candidate vetting process',
      'Cultural alignment assessment',
      'Salary negotiation support',
      '90-day replacement guarantee',
      'Onboarding success coaching',
    ],
    category: 'Staffing',
  },
  {
    id: 'it-consulting',
    icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
    title: 'IT Consulting',
    tagline: 'Strategic technology guidance for enterprise',
    description: 'Our IT consulting practice provides strategic technology advisory services to help Fortune 500 organizations navigate complex digital transformations, technology modernization, and enterprise architecture challenges.',
    features: [
      'Digital transformation strategy',
      'Technology roadmap development',
      'Enterprise architecture consulting',
      'Cloud migration advisory',
      'IT governance & risk management',
      'Vendor selection & management',
    ],
    category: 'Consulting',
  },
  {
    id: 'workforce-solutions',
    icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-2 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
    title: 'Workforce Solutions',
    tagline: 'End-to-end talent program management',
    description: 'For organizations with high-volume or complex talent needs, our Workforce Solutions offering provides comprehensive managed staffing programs, vendor management systems, and strategic workforce planning.',
    features: [
      'Managed staffing programs (MSP)',
      'Vendor management system (VMS)',
      'Strategic workforce planning',
      'Talent analytics & reporting',
      'Diversity & inclusion initiatives',
      'Total talent management',
    ],
    category: 'Consulting',
  },
];

const techSkills = [
  'Cloud (AWS, Azure, GCP)', 'Cybersecurity', 'Data Science & AI/ML',
  'DevOps & SRE', 'Full Stack Development', 'Mobile Development',
  'Enterprise Architecture', 'SAP & ERP', 'Salesforce',
  'Java / .NET / Python', 'React / Angular / Vue', 'Blockchain',
  'IoT Solutions', 'Network Engineering', 'Database Administration',
  'Project Management', 'Business Analysis', 'QA & Testing',
];

const categories = ['All', 'Staffing', 'Executive', 'Consulting'];

export default function ServicesPage() {
  const [activeCategory, setActiveCategory] = useState('All');

  const filtered = activeCategory === 'All'
    ? services
    : services.filter((s) => s.category === activeCategory);

  return (
    <div className="min-h-screen" style={{ background: '#ffffff' }}>
      <Navbar />

      {/* Page Hero - Pinnacle1 style */}
      <section
        className="relative overflow-hidden"
        style={{
          paddingTop: '144px',
          paddingBottom: '80px',
          background: 'linear-gradient(135deg, #01142D 0%, #0A1F3D 60%, #1F65AB 100%)',
        }}
      >
        <div
          className="absolute right-0 bottom-0 select-none pointer-events-none"
          style={{
            fontSize: '320px',
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 700,
            color: 'rgba(0,167,255,0.05)',
            lineHeight: 1,
          }}
        >
          SV
        </div>
        <div className="container-custom relative z-10">
          <div className="flex items-center gap-2 mb-4">
            <Link
              href="/"
              style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', color: 'rgba(255,255,255,0.5)' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#ffffff'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.5)'; }}
            >
              Home
            </Link>
            <span style={{ color: 'rgba(255,255,255,0.3)' }}>/</span>
            <span style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', color: '#00A7FF' }}>Services</span>
          </div>
          <h1
            className="mb-4"
            style={{
              fontFamily: 'Poppins, sans-serif',
              fontSize: 'clamp(36px, 5vw, 64px)',
              fontWeight: 200,
              color: '#ffffff',
              lineHeight: 1.2,
            }}
          >
            Our Services
          </h1>
          <div style={{ width: '60px', height: '3px', background: '#00A7FF', marginBottom: '24px' }}></div>
          <p
            className="max-w-2xl"
            style={{ fontFamily: 'Poppins, sans-serif', fontSize: '18px', color: 'rgba(255,255,255,0.75)', lineHeight: 1.7 }}
          >
            Comprehensive IT staffing and consulting solutions designed to meet the complex talent needs of Fortune 500 organizations — from contract staffing to executive search and beyond.
          </p>
        </div>
      </section>

      {/* Filter Tabs - Pinnacle1 style: clean white bar */}
      <section
        className="sticky z-40"
        style={{
          top: '144px',
          background: '#ffffff',
          borderBottom: '2px solid #00A7FF',
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        }}
      >
        <div className="container-custom">
          <div className="flex items-center gap-0 overflow-x-auto">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className="px-6 py-4 text-sm font-semibold whitespace-nowrap transition-all duration-200"
                style={{
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '14px',
                  fontWeight: activeCategory === cat ? 600 : 400,
                  color: activeCategory === cat ? '#ffffff' : '#48494B',
                  background: activeCategory === cat ? '#00A7FF' : 'transparent',
                  borderBottom: activeCategory === cat ? 'none' : 'none',
                }}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid - Pinnacle1 style: white bg, clean cards */}
      <section className="section-padding" style={{ background: '#ffffff' }}>
        <div className="container-custom">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map((service, i) => (
              <div
                key={service.id}
                className="group flex flex-col transition-all duration-300"
                style={{
                  background: '#ffffff',
                  border: '1px solid #e8e8e8',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 32px rgba(0,145,221,0.15)';
                  (e.currentTarget as HTMLElement).style.borderColor = 'rgba(0,167,255,0.3)';
                  (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)';
                  (e.currentTarget as HTMLElement).style.borderColor = '#e8e8e8';
                  (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                }}
              >
                {/* Top color bar */}
                <div
                  style={{
                    height: '4px',
                    background: i % 2 === 0 ? '#00A7FF' : '#01142D',
                  }}
                />
                <div className="p-8 flex flex-col flex-1">
                  <div
                    className="w-14 h-14 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300"
                    style={{ background: i % 2 === 0 ? '#00A7FF' : '#01142D' }}
                  >
                    <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d={service.icon} />
                    </svg>
                  </div>
                  <span
                    className="inline-block mb-3 px-3 py-1 text-xs font-semibold self-start"
                    style={{
                      background: 'rgba(0,167,255,0.1)',
                      color: '#0091DD',
                      fontFamily: 'Montserrat, sans-serif',
                    }}
                  >
                    {service.category}
                  </span>
                  <h3
                    className="mb-2"
                    style={{ fontFamily: 'Poppins, sans-serif', fontSize: '22px', fontWeight: 400, color: 'rgba(0,0,0,0.8)' }}
                  >
                    {service.title}
                  </h3>
                  <p
                    className="mb-4 italic"
                    style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', color: '#00A7FF' }}
                  >
                    {service.tagline}
                  </p>
                  <p
                    className="mb-6 flex-1"
                    style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', color: '#48494B', lineHeight: 1.7 }}
                  >
                    {service.description}
                  </p>
                  <div className="space-y-2 mb-6">
                    {service.features.map((feature, fi) => (
                      <div key={feature} className="flex items-center gap-2">
                        <div
                          className="w-5 h-5 flex items-center justify-center flex-shrink-0"
                          style={{ background: fi % 2 === 0 ? 'rgba(0,167,255,0.15)' : 'rgba(1,20,45,0.08)' }}
                        >
                          <svg
                            className="w-3 h-3"
                            style={{ color: fi % 2 === 0 ? '#00A7FF' : '#01142D' }}
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2.5}
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span
                          style={{ fontFamily: 'Poppins, sans-serif', fontSize: '13px', color: '#48494B' }}
                        >
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>
                  <Link
                    href="/contact"
                    className="inline-flex items-center justify-center px-6 py-3 font-semibold transition-all duration-300 hover:opacity-90 mt-auto"
                    style={{ background: '#00A7FF', color: '#ffffff', fontFamily: 'Montserrat, sans-serif', fontSize: '13px' }}
                  >
                    Request This Service
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Approach Banner - Pinnacle1 style */}
      <section className="relative w-full overflow-hidden" style={{ height: '480px' }}>
        <img
          src="/assets/images/executives-discussion-bg.png"
          alt="Business executives and managers engaged in a strategic discussion around a conference table in a modern corporate office"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0" style={{ background: 'rgba(1,20,45,0.82)' }}></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
          <h6
            className="mb-4"
            style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '12px',
              fontWeight: 600,
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              color: '#00A7FF',
            }}
          >
            Our Approach
          </h6>
          <h2
            className="mb-4 max-w-2xl"
            style={{ fontFamily: 'Poppins, sans-serif', fontSize: '36px', fontWeight: 200, color: '#ffffff', lineHeight: 1.3 }}
          >
            Precision Talent Delivery for Complex Organizations
          </h2>
          <div style={{ width: '48px', height: '3px', background: '#00A7FF', marginBottom: '20px' }}></div>
          <p
            className="max-w-xl"
            style={{ fontFamily: 'Poppins, sans-serif', fontSize: '18px', color: 'rgba(255,255,255,0.75)', lineHeight: 1.7 }}
          >
            Every engagement starts with a deep understanding of your business goals, technology stack, and team culture — ensuring every candidate we present is the RIGHT fit.
          </p>
        </div>
      </section>

      {/* Tech Skills Section - Pinnacle1 style: light bg */}
      <section className="section-padding" style={{ background: '#FAFAFA' }}>
        <div className="container-custom">
          <div className="text-center mb-12">
            <h6
              className="mb-3"
              style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px', fontWeight: 600, color: '#1F65AB', letterSpacing: '0.1em', textTransform: 'uppercase' }}
            >
              Technology Expertise
            </h6>
            <h2
              className="mb-4"
              style={{ fontFamily: 'Poppins, sans-serif', fontSize: '40px', fontWeight: 100, color: 'rgba(0,0,0,0.6)' }}
            >
              Skills &amp; Technology Domains
            </h2>
            <div style={{ width: '48px', height: '3px', background: '#00A7FF', margin: '0 auto 16px' }}></div>
            <p
              className="max-w-2xl mx-auto"
              style={{ fontFamily: 'Poppins, sans-serif', fontSize: '16px', color: '#48494B', lineHeight: 1.7 }}
            >
              We place IT professionals across all major technology domains, from cloud infrastructure to emerging technologies.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 justify-center">
            {techSkills.map((skill, i) => (
              <span
                key={skill}
                className="px-4 py-2 text-sm font-medium transition-all duration-200"
                style={{
                  background: i % 3 === 0 ? '#00A7FF' : i % 3 === 1 ? '#01142D' : '#ffffff',
                  color: i % 3 === 2 ? '#48494B' : '#ffffff',
                  border: i % 3 === 2 ? '1px solid #e8e8e8' : 'none',
                  fontFamily: 'Poppins, sans-serif',
                }}
              >
                {skill}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="py-20"
        style={{ background: 'linear-gradient(135deg, #01142D 0%, #0A1F3D 100%)' }}
      >
        <div className="container-custom text-center">
          <h2
            className="mb-4"
            style={{ fontFamily: 'Poppins, sans-serif', fontSize: '40px', fontWeight: 200, color: '#ffffff', lineHeight: 1.3 }}
          >
            Ready to Find the RIGHT IT Talent?
          </h2>
          <div style={{ width: '48px', height: '3px', background: '#00A7FF', margin: '0 auto 24px' }}></div>
          <p
            className="mb-8 max-w-xl mx-auto"
            style={{ fontFamily: 'Poppins, sans-serif', fontSize: '18px', color: 'rgba(255,255,255,0.7)', lineHeight: 1.7 }}
          >
            Contact our team today to discuss your specific talent needs and discover how TranceformRIGHT can help.
          </p>
          <Link
            href="/contact"
            className="inline-flex items-center justify-center px-10 py-4 font-semibold transition-all duration-300 hover:opacity-90"
            style={{ background: '#00A7FF', color: '#ffffff', fontFamily: 'Montserrat, sans-serif', fontSize: '15px' }}
          >
            Contact Us Today
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
